import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Tabs,
  Text,
  Badge,
  Button,
  StatsGrid,
  StatusDot,
  VersionTimeline,
  Modal,
  Input,
} from '@teals/ui-web';
import type { TabItem, VersionEntry, StatItem } from '@teals/ui-web';
import {
  useGetAgentQuery,
  useGetAgentsQuery,
  usePublishAgentMutation,
  useCreateAgentVersionMutation,
  useSendMessageMutation,
  useGetSessionHistoryQuery,
  useGetOrgModelsQuery,
  type Agent,
  type AgentDetail as AgentDetailType,
} from '../../store/api';
import type { FilterConfig, FilterParameter } from '../../store/api/dataSourcesApi';
import {
  describeFilterScope,
  formatDatasourceQueryLog,
  formatCodeExecutionLog,
  paramUsesListBinding,
  toChatQueryParameters,
} from '../DataSources/filterConfig';
import { formatMcpToolLog } from '../../store/api/mcpIntegrationsApi';
import { dataSourceInstancePath, mcpInstancePath } from '../Integrations/paths';
import ScopedAnalyticsPanel from '../Analytics/ScopedAnalyticsPanel';
import SessionsTab from './SessionsTab';
import ConfigureTab from './ConfigureTab';
import {
  autoModelReasonLine,
  autoReasonFromEventData,
} from './autoModelReason';
import { useVoiceChat } from '../../hooks/useVoiceChat';
import VoiceChatBar, { VoiceMicButton } from '../../components/VoiceChatBar';
import {
  AgentSystemSummary,
  PartOfSystemCard,
  HintChips,
  MembershipChip,
  CapabilitiesRow,
  typeBadgeLabel,
  isManagementAgent,
} from './AgentSystemPanels';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import {
  modelSupportsEffort,
} from '../../utils/modelDisplay';
import ModelPicker from '../../components/ModelPicker';
import { assistantDisplayText } from '../../utils/voiceText';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { useAuth } from '../../hooks/useAuth';

type DetailTab = 'overview' | 'configure' | 'sessions' | 'analytics' | 'playground';

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  conversationId?: string;
}

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'configure', label: 'Configure' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'playground', label: 'Playground' },
];

const VALID_TABS: DetailTab[] = [
  'overview',
  'configure',
  'sessions',
  'analytics',
  'playground',
];

function playgroundFilterParams(
  agent: Agent | AgentDetailType | undefined,
): FilterParameter[] {
  const seen = new Set<string>();
  const out: FilterParameter[] = [];
  for (const ds of agent?.dataSources || []) {
    for (const param of ds.filterConfig?.parameters || []) {
      if (!param.name || seen.has(param.name)) continue;
      const source = param.source || param.name;
      const impersonatable =
        source === 'consumer_org_id' ||
        source === 'consumer_user_id' ||
        source.startsWith('parameters.');
      if (!impersonatable) continue;
      seen.add(param.name);
      out.push(param);
    }
  }
  return out;
}

function callerTokenMcps(agent: Agent | AgentDetailType | undefined) {
  return (agent?.mcpServers || []).filter(
    (row) => (row.authMode || 'caller_token') === 'caller_token' && row.mcpId,
  );
}

function mcpTokenLabel(row: {
  name: string;
  callerTokenIn?: string | null;
  payloadParameter?: string | null;
}): string {
  const param = row.payloadParameter || 'x-cv-mcp-token';
  const loc = row.callerTokenIn === 'body' ? `parameters.${param}` : `header ${param}`;
  return `${row.name} · ${loc}`;
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.25rem;

  &:hover {
    color: var(--accent);
  }
`;

const AgentHeader = styled.div`
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--border);
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  align-items: flex-end;
`;

const VersionField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const VersionSelect = styled.select`
  font-size: 0.8125rem;
  padding: 0.4rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--border);
  background: var(--surface, #fff);
  color: var(--text-primary);
  max-width: 16rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TabContent = styled.div`
  margin-top: 0.5rem;
`;

const OverviewPanels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionLabel = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const MetaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MetaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MetaItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const MetaKey = styled.span`
  color: var(--text-muted);
`;

const MetaValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
`;

const PartnerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);

  @media (max-width: 1023px) {
    height: min(500px, 55dvh);
  }
`;

const JsonPre = styled.pre`
  margin: 0;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PlaygroundLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const InspectorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 1rem;
  background: var(--bg-surface);
  min-height: 500px;
  max-height: 500px;
  overflow: auto;

  @media (max-width: 1023px) {
    min-height: min(500px, 50dvh);
    max-height: none;
  }
`;

const InspectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const InspectorTitle = styled.h5`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
`;

const EventLog = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-secondary);
`;

const FilterScopeNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  background: var(--bg-elevated, rgba(148, 163, 184, 0.12));
  color: var(--text-secondary);
  font-size: 0.8125rem;
`;

const PendingDots = styled.span`
  display: inline-flex;
  gap: 0.25rem;

  span {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: var(--accent, #14b8a6);
    animation: playgroundPendingPulse 1.2s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes playgroundPendingPulse {
    0%,
    80%,
    100% {
      opacity: 0.35;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-2px);
    }
  }
`;

const SystemLine = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const ChatBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--bg-elevated, #f8fafc);
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary);
  flex-wrap: wrap;
`;

const ModelSelect = styled.select`
  font-size: 0.75rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  max-width: 14rem;
`;

const ModelPickerTrigger = styled.button`
  font-size: 0.75rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.25rem 0.625rem;
  max-width: 16rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    border-color: var(--accent, #2d9a8f);
  }

  &:focus-visible {
    outline: 2px solid var(--accent, #2d9a8f);
    outline-offset: 1px;
  }
`;

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
`;

const SoftChip = styled.button`
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 75%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-primary)')};
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: transparent;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const PageMessage = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ModalCopy = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-secondary);

  code {
    font-size: 0.8125rem;
  }
`;

const SCOPE_BADGES: Record<
  string,
  { variant: 'primary' | 'info' | 'warning' | 'neutral'; label: string }
> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
  system: { variant: 'neutral', label: 'System' },
};

function stripVersionPrefix(label: string): string {
  return label.replace(/^v/i, '').trim();
}

function formatVersionLabel(label: string | null | undefined): string {
  if (!label || label === '—') return '';
  const bare = stripVersionPrefix(label);
  return bare ? `v${bare}` : '';
}

/** Suggest next minor semver from published_version or version history. */
function suggestNextVersion(agent: AgentDetailType): string {
  const published =
    agent.publishedVersion ||
    agent.versions.find((v) => v.status === 'published' && v.isLatest)?.version ||
    agent.versions.find((v) => v.status === 'published')?.version ||
    (agent.versionStatus === 'published' ? agent.version : undefined) ||
    agent.versions[0]?.version ||
    '1.0';
  const bare = stripVersionPrefix(String(published));
  const parts = bare.split('.');
  if (parts.length >= 2) {
    const major = parts[0] || '1';
    const minor = Number.parseInt(parts[1] || '0', 10);
    const nextMinor = Number.isFinite(minor) ? minor + 1 : 1;
    return `${major}.${nextMinor}`;
  }
  const major = Number.parseInt(parts[0] || '1', 10);
  if (Number.isFinite(major)) return `${major}.1`;
  return '1.1';
}

function canPublishAgent(agent: Agent): boolean {
  return (
    agent.versionStatus === 'draft' ||
    agent.versionStatus === 'sandbox' ||
    Boolean(agent.hasOpenDraft)
  );
}

function versionStatusBadgeText(agent: Agent): string {
  if (agent.versionStatus === 'draft' || (agent.hasOpenDraft && agent.draftVersion)) {
    const label =
      formatVersionLabel(agent.draftVersion || agent.version) || agent.version;
    return `Draft ${label}`.replace(/\s+/g, ' ').trim();
  }
  if (
    agent.versionStatus === 'sandbox' ||
    (agent.hasOpenDraft && agent.sandboxVersion)
  ) {
    const label =
      formatVersionLabel(agent.sandboxVersion || agent.version) || agent.version;
    return `Sandbox ${label}`.replace(/\s+/g, ' ').trim();
  }
  const pub =
    formatVersionLabel(agent.publishedVersion || agent.version) || agent.version;
  const showLatest =
    agent.isLatest === true ||
    (agent.versionStatus === 'published' && !agent.hasOpenDraft);
  if (showLatest && pub) {
    return `Published ${pub} · latest`;
  }
  return pub ? `Published ${pub}` : 'Published';
}

function versionsMatch(a: string, b: string): boolean {
  return stripVersionPrefix(a).toLowerCase() === stripVersionPrefix(b).toLowerCase();
}

function resolveSelectedVersion(
  agent: AgentDetailType,
  requested: string | null,
): AgentDetailType['versions'][number] | undefined {
  const versions = agent.versions || [];
  if (requested) {
    const match = versions.find((entry) => versionsMatch(entry.version, requested));
    if (match) return match;
  }
  return (
    versions.find((entry) => entry.status === 'draft') ||
    versions.find((entry) => entry.status === 'sandbox') ||
    versions.find((entry) => entry.status === 'published' && entry.isLatest) ||
    versions.find((entry) => entry.status === 'published') ||
    versions[0]
  );
}

function applyVersionSnapshot(
  agent: AgentDetailType,
  version?: AgentDetailType['versions'][number],
): AgentDetailType {
  if (!version?.config) return agent;
  const config = { ...agent.config, ...version.config };
  return {
    ...agent,
    config,
    llmModel: config.model || agent.llmModel,
    recommendedModels: config.recommendedModels ?? agent.recommendedModels,
  };
}

function versionOptionLabel(entry: AgentDetailType['versions'][number]): string {
  const label = formatVersionLabel(entry.version) || entry.version;
  if (entry.status === 'draft') return `${label} · Draft`;
  if (entry.status === 'sandbox') return `${label} · Sandbox`;
  if (entry.status === 'archived') return `${label} · Previous`;
  if (entry.isLatest) return `${label} · Published · latest`;
  return `${label} · Published`;
}

function snapshotLabelFor(
  entry?: AgentDetailType['versions'][number],
): string | undefined {
  if (!entry || entry.status === 'draft' || entry.status === 'sandbox') {
    return undefined;
  }
  const label = formatVersionLabel(entry.version) || entry.version;
  if (entry.status === 'archived') return `previous ${label}`;
  return `published ${label}`;
}

function cloneSourceLabel(
  agent: AgentDetailType,
  selected?: AgentDetailType['versions'][number],
): string | undefined {
  if (
    selected &&
    (selected.status === 'published' || selected.status === 'archived')
  ) {
    return selected.version;
  }
  return agent.publishedVersion || undefined;
}

function playgroundAgentVersion(agent: Agent): string | undefined {
  if (
    agent.versionStatus === 'draft' ||
    agent.versionStatus === 'sandbox' ||
    agent.hasOpenDraft
  ) {
    const raw =
      agent.draftVersion ||
      agent.sandboxVersion ||
      (agent.version !== '—' ? agent.version : undefined);
    return raw || undefined;
  }
  return undefined;
}

function playgroundGreeting(agent: Agent): string {
  return isManagementAgent(agent)
    ? `Hello! I'm ${agent.name}. Upload a document or ask a follow-up — I'll fan out to specialists and return structured results.`
    : `Hello! I'm ${agent.name}. How can I help you today?`;
}

function browserTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.trim() ? zone.trim() : 'UTC';
  } catch {
    return 'UTC';
  }
}

function eventConversationId(event: ExchangeEvent): string | undefined {
  const data = event.data || {};
  const raw =
    data.conversation_id ||
    data.conversationId ||
    (event.type === 'conversation.result' ? event.exchange_id : undefined);
  const value = raw == null ? '' : String(raw).trim();
  return value || undefined;
}

/** Pull a human message from nested portal/API error payloads (RTK + Underpin). */
function extractPortalChatError(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const data = (err as { data?: unknown }).data;
  const candidates: unknown[] = [data];
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    candidates.push(d.message, d['error-details'], d.detail);
  }
  for (const c of candidates) {
    if (typeof c !== 'string' || !c.trim()) continue;
    let text = c.trim();
    // Nested JSON strings from Underpin wrappers
    for (let i = 0; i < 3; i += 1) {
      if (!text.startsWith('{')) break;
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const next =
          (typeof parsed.message === 'string' && parsed.message) ||
          (typeof parsed['error-details'] === 'string' &&
            parsed['error-details']) ||
          null;
        if (!next) break;
        text = next;
      } catch {
        break;
      }
    }
    // Prefer the actionable Bedrock / LLM clause when present
    const bedrockIdx = text.indexOf('Bedrock Converse failed:');
    if (bedrockIdx >= 0) {
      return text.slice(bedrockIdx).replace(/^Error::\s*/i, '').trim();
    }
    const cleaned = text.replace(/^Error::\s*/i, '').trim();
    if (cleaned && cleaned !== 'NA') return cleaned;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

const AgentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DetailTab) || 'overview';
  const deepLinkSessionId = searchParams.get('sessionId') || undefined;
  const [activeTab, setActiveTab] = useState<DetailTab>(
    VALID_TABS.includes(initialTab) ? initialTab : 'overview',
  );
  const [config, setConfig] = useState<AgentConfig>({
    systemPrompt: '',
    model: 'gpt-4o',
    temperature: 0.7,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(deepLinkSessionId);
  const [hydratedSessionId, setHydratedSessionId] = useState<string | undefined>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createVersionOpen, setCreateVersionOpen] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newVersionNote, setNewVersionNote] = useState('');
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [wsEvents, setWsEvents] = useState<ExchangeEvent[]>([]);
  const [llmLog, setLlmLog] = useState<string[]>([]);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [modelOverride, setModelOverride] = useState('');
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [playgroundModelSearch, setPlaygroundModelSearch] = useState('');
  const [effortOverride, setEffortOverride] = useState<'' | 'low' | 'medium' | 'high'>('');
  const [consumerOrgId, setConsumerOrgId] = useState('');
  const [consumerUserId, setConsumerUserId] = useState('');
  const [queryParameters, setQueryParameters] = useState<Record<string, string>>({});
  const [mcpTokenValues, setMcpTokenValues] = useState<Record<string, string>>({});
  const [resolvedModel, setResolvedModel] = useState<string | null>(null);
  const [autoModelReason, setAutoModelReason] = useState<string | null>(null);
  const [resolvedEffort, setResolvedEffort] = useState<string | null>(null);
  const [resolvedVersion, setResolvedVersion] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const seenReplyKeysRef = useRef<Set<string>>(new Set());

  const { data: agent, isLoading, isError } = useGetAgentQuery(id, {
    skip: !id || id === 'new',
  });
  const requestedVersion = searchParams.get('version');
  const selectedVersion = useMemo(
    () => (agent ? resolveSelectedVersion(agent, requestedVersion) : undefined),
    [agent, requestedVersion],
  );
  const viewedAgent = useMemo(
    () => (agent ? applyVersionSnapshot(agent, selectedVersion) : undefined),
    [agent, selectedVersion],
  );
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: catalogAgents = [] } = useGetAgentsQuery(
    { orgId: orgId || '' },
    { skip: !orgId },
  );
  const { data: orgModels } = useGetOrgModelsQuery(orgId!, { skip: !orgId });
  const [publishAgent] = usePublishAgentMutation();
  const [createAgentVersion] = useCreateAgentVersionMutation();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const showPending = isSending || awaitingReply;
  const submitQueryRef = useRef<(text: string) => Promise<void>>(async () => {});
  const speakRef = useRef<(text: string) => void>(() => {});
  const handleVoiceTranscript = useCallback((text: string) => {
    void submitQueryRef.current(text);
  }, []);
  const {
    voiceState,
    error: voiceError,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  } = useVoiceChat({
    onTranscript: handleVoiceTranscript,
    voice: agent?.config?.voice?.voice,
    model: agent?.config?.voice?.model,
  });
  speakRef.current = speak;

  const appendPlaygroundAssistant = useCallback(
    (reply: {
      content: string;
      conversationId?: string;
      id?: string;
      speakText?: string;
    }) => {
      const display = reply.content.trim();
      if (!display) {
        setAwaitingReply(false);
        return;
      }
      const conversationId = (reply.conversationId || '').trim();
      const idKey = conversationId ? `cid:${conversationId}` : '';
      const textKey = `text:${display}`;
      const seen = seenReplyKeysRef.current;
      if ((idKey && seen.has(idKey)) || seen.has(textKey)) {
        setAwaitingReply(false);
        return;
      }
      if (idKey) seen.add(idKey);
      seen.add(textKey);
      setAwaitingReply(false);
      setMessages((prev) => {
        if (
          (conversationId &&
            prev.some(
              (m) =>
                m.role === 'assistant' && m.conversationId === conversationId,
            )) ||
          prev.some((m) => m.role === 'assistant' && m.content === display)
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            id: reply.id || `msg-${Date.now()}`,
            role: 'assistant' as const,
            content: display,
            ...(conversationId ? { conversationId } : {}),
          },
        ];
      });
      void speakRef.current(reply.speakText ?? display);
    },
    [],
  );

  const {
    subscribe,
    unsubscribe,
    addListener,
    removeListener,
    isConnected,
    connectionState,
  } = useWebSocketContext();

  const { data: playgroundHistory } = useGetSessionHistoryQuery(
    deepLinkSessionId || '',
    {
      skip:
        activeTab !== 'playground' ||
        !deepLinkSessionId ||
        hydratedSessionId === deepLinkSessionId,
    },
  );

  const readOnly = Boolean(agent?.readOnly);

  const dsFilterParams = useMemo(
    () => playgroundFilterParams(agent),
    [agent],
  );

  const dsFilterConfigs = useMemo(
    () =>
      (agent?.dataSources || [])
        .map((ds) => ds.filterConfig)
        .filter((config): config is FilterConfig => Boolean(config)),
    [agent],
  );

  const searchScope = useMemo(
    () =>
      dsFilterConfigs
        .map((config) => describeFilterScope(config))
        .filter(Boolean)
        .join('\n'),
    [dsFilterConfigs],
  );

  const missingRequiredFilters = useMemo(() => {
    return dsFilterParams.filter((param) => {
      if (!param.required) return false;
      const source = param.source || param.name;
      if (source === 'consumer_org_id') return !consumerOrgId.trim();
      if (source === 'consumer_user_id') return !consumerUserId.trim();
      if (source.startsWith('parameters.')) {
        const key = source.slice('parameters.'.length);
        return !(queryParameters[key] || '').trim();
      }
      return false;
    });
  }, [dsFilterParams, consumerOrgId, consumerUserId, queryParameters]);

  const playgroundMcps = useMemo(() => callerTokenMcps(agent), [agent]);
  const missingMcpTokens = useMemo(
    () =>
      playgroundMcps.filter((row) => !(mcpTokenValues[row.mcpId] || '').trim()),
    [playgroundMcps, mcpTokenValues],
  );

  /** Default | Auto | recommended ∪ org-enabled chat product ids (exclude agent default). */
  const playgroundModelCatalog = useMemo(() => {
    const defaultModel = agent?.llmModel || agent?.config?.model || '';
    const recommended =
      agent?.recommendedModels || agent?.config?.recommendedModels || [];
    const byId = new Map<string, OrgModel>();
    for (const m of orgModels || []) {
      if (!m.enabled || m.chatCapable === false) continue;
      byId.set(m.productId, m);
    }
    const seen = new Set<string>();
    const models: OrgModel[] = [];
    for (const id of [...recommended, ...byId.keys()]) {
      const pid = String(id || '').trim();
      if (!pid || pid === defaultModel || seen.has(pid)) continue;
      seen.add(pid);
      const known = byId.get(pid);
      models.push(
        known || {
          productId: pid,
          displayName: pid,
          provider: '',
          capabilities: [],
          enabled: true,
          chatCapable: true,
        },
      );
    }
    return models;
  }, [
    agent?.llmModel,
    agent?.config?.model,
    agent?.recommendedModels,
    agent?.config?.recommendedModels,
    orgModels,
  ]);

  const playgroundModelTriggerLabel = useMemo(() => {
    const defaultModel = agent?.llmModel || agent?.config?.model || '—';
    if (!modelOverride) return `Default (${defaultModel})`;
    if (modelOverride === 'auto') return 'Auto';
    const known = playgroundModelCatalog.find(
      (m) => m.productId === modelOverride,
    );
    if (known) return known.displayName || known.productId;
    return modelOverride;
  }, [
    agent?.llmModel,
    agent?.config?.model,
    modelOverride,
    playgroundModelCatalog,
  ]);

  const closeModelPicker = () => {
    setModelPickerOpen(false);
    setPlaygroundModelSearch('');
  };

  const selectPlaygroundModel = (value: string) => {
    setModelOverride(value);
    closeModelPicker();
  };

  const playgroundEffortModelId =
    modelOverride && modelOverride !== 'auto'
      ? modelOverride
      : agent?.llmModel || agent?.config?.model || '';
  const showPlaygroundEffort = modelSupportsEffort(
    (orgModels || []).find((m) => m.productId === playgroundEffortModelId)
      ?.capabilities,
  );

  useEffect(() => {
    if (!showPlaygroundEffort && effortOverride) {
      setEffortOverride('');
    }
  }, [showPlaygroundEffort, effortOverride]);

  useEffect(() => {
    if (agent?.config) {
      setConfig({
        systemPrompt: agent.config.systemPrompt || '',
        model: agent.config.model || 'gpt-4o',
        temperature: agent.config.temperature ?? 0.7,
      });
    }
  }, [agent?.id, agent?.config]);

  useEffect(() => {
    if (activeTab !== 'playground' || !sessionId || !agent) return;
    subscribe(sessionId, agent.agentName);
    return () => {
      unsubscribe();
    };
  }, [activeTab, sessionId, agent?.agentName, subscribe, unsubscribe]);

  useEffect(() => {
    if (activeTab !== 'playground') return;
    const listener = (event: ExchangeEvent) => {
      setWsEvents((prev) => [event, ...prev].slice(0, 40));
      if (
        event.type === 'datasource.query.started' ||
        event.type === 'datasource.query.completed' ||
        event.type === 'datasource.query.failed'
      ) {
        setLlmLog((prev) =>
          [formatDatasourceQueryLog(event), ...prev].slice(0, 30),
        );
        if (event.type === 'datasource.query.failed' && event.data?.error) {
          const errText = `Data source search did not run: ${String(event.data.error)}`;
          setMessages((prev) => {
            if (prev.some((m) => m.content === errText)) return prev;
            return [
              ...prev,
              {
                id: event.exchange_id || `ds-err-${Date.now()}`,
                role: 'assistant',
                content: errText,
              },
            ];
          });
        }
      }
      if (
        event.type === 'mcp.tool.started' ||
        event.type === 'mcp.tool.completed' ||
        event.type === 'mcp.tool.failed'
      ) {
        setLlmLog((prev) => [formatMcpToolLog(event), ...prev].slice(0, 30));
      }
      if (
        event.type === 'code.execution.started' ||
        event.type === 'code.execution.output' ||
        event.type === 'code.execution.retrying' ||
        event.type === 'code.execution.completed' ||
        event.type === 'code.execution.failed'
      ) {
        setLlmLog((prev) => [formatCodeExecutionLog(event), ...prev].slice(0, 30));
      }
      if (
        event.type === 'exchange.step.completed' ||
        event.type === 'exchange.thinking' ||
        event.type === 'exchange.subagent.started' ||
        event.type === 'exchange.subagent.completed'
      ) {
        const label =
          event.data?.step ||
          event.data?.model ||
          event.data?.agent_name ||
          event.type;
        setLlmLog((prev) => [`${event.type}: ${String(label)}`, ...prev].slice(0, 30));
        if (event.data?.resolved_model) {
          setResolvedModel(String(event.data.resolved_model));
        }
        if (event.data?.model_selection || event.data?.modelSelection) {
          setAutoModelReason(autoReasonFromEventData(event.data));
        }
        if (event.data?.resolved_effort) {
          setResolvedEffort(String(event.data.resolved_effort));
        }
        const versionLabel =
          event.data?.resolved_agent_version ||
          event.data?.agent_version ||
          event.data?.['agent-version'];
        if (versionLabel) {
          setResolvedVersion(String(versionLabel));
        }
      }
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        const display = assistantDisplayText(content);
        if (!display) {
          if (event.type === 'conversation.result') {
            setAwaitingReply(false);
          }
          return;
        }
        appendPlaygroundAssistant({
          content: display,
          conversationId: eventConversationId(event),
          id: event.exchange_id || `ws-${Date.now()}`,
          speakText: display,
        });
        return;
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        const errText = String(
          event.data?.error || event.data?.message || 'Agent processing failed.',
        );
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: event.exchange_id || `ws-err-${Date.now()}`,
            role: 'assistant',
            content: errText,
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [activeTab, addListener, removeListener, appendPlaygroundAssistant]);

  useEffect(() => {
    if (agent && !deepLinkSessionId) {
      seenReplyKeysRef.current.clear();
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: playgroundGreeting(agent),
        },
      ]);
      setSessionId(undefined);
      setHydratedSessionId(undefined);
      setWsEvents([]);
      setLlmLog([]);
    }
  }, [agent?.id, agent?.name, deepLinkSessionId]);

  useEffect(() => {
    if (deepLinkSessionId) {
      setSessionId(deepLinkSessionId);
      setActiveTab('playground');
    }
  }, [deepLinkSessionId]);

  useEffect(() => {
    if (
      playgroundHistory &&
      deepLinkSessionId &&
      playgroundHistory.sessionId === deepLinkSessionId &&
      hydratedSessionId !== deepLinkSessionId
    ) {
      const hydrated: ChatMessage[] = playgroundHistory.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m, idx) => ({
          id: `hist-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
      setMessages(
        hydrated.length
          ? hydrated
          : [
              {
                id: 'greeting',
                role: 'assistant',
                content: `Continuing session ${deepLinkSessionId}. How can I help?`,
              },
            ],
      );
      setHydratedSessionId(deepLinkSessionId);
    }
  }, [playgroundHistory, deepLinkSessionId, hydratedSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const resetPlaygroundLocalState = () => {
    seenReplyKeysRef.current.clear();
    setSessionId(undefined);
    setHydratedSessionId(undefined);
    setWsEvents([]);
    setLlmLog([]);
    setAwaitingReply(false);
    setResolvedModel(null);
    setAutoModelReason(null);
    setResolvedEffort(null);
    setResolvedVersion(null);
    setModelOverride('');
    setEffortOverride('');
    setConsumerOrgId('');
    setConsumerUserId('');
    setQueryParameters({});
    setMcpTokenValues({});
    if (agent) {
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: playgroundGreeting(agent),
        },
      ]);
    }
  };

  const handleTabChange = (tab: string, extra?: Record<string, string>) => {
    const next = tab as DetailTab;
    setActiveTab(next);
    if (next === 'playground' && !extra?.sessionId) {
      resetPlaygroundLocalState();
    }
    const params: Record<string, string> = { ...extra };
    const version = searchParams.get('version');
    if (version) params.version = version;
    if (next !== 'overview') params.tab = next;
    setSearchParams(params);
  };

  const selectVersion = (version: string) => {
    const params: Record<string, string> = { version };
    if (activeTab !== 'overview') params.tab = activeTab;
    const session = searchParams.get('sessionId');
    if (session && activeTab === 'playground') params.sessionId = session;
    setSearchParams(params);
  };

  const openPlaygroundWithSession = (sid: string) => {
    setSessionId(sid);
    setHydratedSessionId(undefined);
    handleTabChange('playground', { sessionId: sid });
  };

  const startNewPlaygroundSession = () => {
    resetPlaygroundLocalState();
    handleTabChange('playground');
  };

  const handleOpenCreateVersion = () => {
    if (!agent || readOnly) return;
    setNewVersion(suggestNextVersion(agent));
    setNewVersionNote('');
    setActionError(null);
    setCreateVersionOpen(true);
  };

  const handleCloseCreateVersion = () => {
    setCreateVersionOpen(false);
    setNewVersion('');
    setNewVersionNote('');
  };

  const handleCreateVersion = async () => {
    if (!agent || readOnly) return;
    const version = newVersion.trim();
    if (!version) {
      setActionError('Enter a version number.');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const source = cloneSourceLabel(agent, selectedVersion);
      await createAgentVersion({
        id: agent.id,
        version,
        ...(source ? { source_version: source } : {}),
        ...(newVersionNote.trim()
          ? { description: newVersionNote.trim() }
          : {}),
      }).unwrap();
      const createdLabel = formatVersionLabel(version) || version;
      handleCloseCreateVersion();
      setActiveTab('configure');
      setSearchParams({ tab: 'configure', version: createdLabel });
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to create agent version',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPublishConfirm = () => {
    if (!agent || readOnly || !canPublishAgent(agent)) return;
    setPublishConfirmOpen(true);
  };

  const handlePublish = async () => {
    if (!agent || readOnly || !canPublishAgent(agent)) return;
    setSaving(true);
    setActionError(null);
    try {
      await publishAgent(agent.id).unwrap();
      setPublishConfirmOpen(false);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to publish agent',
      );
    } finally {
      setSaving(false);
    }
  };

  const submitPlaygroundQuery = async (rawText: string) => {
    const text = rawText.trim();
    if (!text || !agent || showPending) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
    };
    if (missingRequiredFilters.length > 0) {
      setActionError(
        `Set required query filters: ${missingRequiredFilters
          .map((p) => p.label || p.name)
          .join(', ')}`,
      );
      return;
    }
    if (missingMcpTokens.length > 0) {
      setActionError(
        `Set required Custom MCP tokens: ${missingMcpTokens
          .map((row) => mcpTokenLabel(row))
          .join(', ')}`,
      );
      return;
    }
    setMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setActionError(null);
    setAwaitingReply(true);

    try {
      const agentVersion = playgroundAgentVersion(agent);
      const parameters = toChatQueryParameters(queryParameters, dsFilterConfigs);
      const extraHeaders: Record<string, string> = {};
      for (const row of playgroundMcps) {
        const value = (mcpTokenValues[row.mcpId] || '').trim();
        if (!value) continue;
        const param = row.payloadParameter || 'x-cv-mcp-token';
        if (row.callerTokenIn === 'body') {
          parameters[param] = value;
        } else {
          extraHeaders[param] = value;
        }
      }
      const result = await sendMessage({
        query: userMsg.content,
        agentName: agent.agentName,
        ...(sessionId
          ? { sessionId }
          : { forceNewSession: true }),
        ...(agentVersion ? { agentVersion } : {}),
        ...(modelOverride ? { model: modelOverride } : {}),
        ...(effortOverride ? { effort: effortOverride } : {}),
        ...(consumerOrgId.trim()
          ? { consumerOrgId: consumerOrgId.trim() }
          : {}),
        ...(consumerUserId.trim()
          ? { consumerUserId: consumerUserId.trim() }
          : {}),
        ...(Object.keys(parameters).length > 0 ? { parameters } : {}),
        ...(Object.keys(extraHeaders).length > 0 ? { extraHeaders } : {}),
        metadata: { timezone: browserTimezone() },
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, agent.agentName);
      }
      if (result.modelSelection) {
        setAutoModelReason(autoModelReasonLine(result.modelSelection.reasonCode));
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) {
        // Keep awaitingReply true until WebSocket delivers the final answer.
        return;
      }
      if (!replyText) {
        setAwaitingReply(false);
        return;
      }
      const display = assistantDisplayText(replyText);
      if (!display) {
        setAwaitingReply(false);
        return;
      }
      appendPlaygroundAssistant({
        content: display,
        conversationId: result.conversationId,
        id: result.exchangeId || `msg-${Date.now() + 1}`,
        speakText: display,
      });
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const apiDetail = extractPortalChatError(err);
      const message =
        status === 404
          ? 'Agent not found or you do not have access to invoke it.'
          : status === 401 || status === 403
            ? 'Portal authentication failed for playground chat.'
            : apiDetail ||
              'Unable to reach the portal chat API. Ensure the agent is available for sandbox testing.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: message,
        },
      ]);
    }
  };
  submitQueryRef.current = submitPlaygroundQuery;

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitPlaygroundQuery(chatInput);
  };

  const stats: StatItem[] = useMemo(() => {
    if (!agent) return [];
    return [
      {
        label: 'Total Sessions',
        value: String(agent.stats.totalSessions.toLocaleString()),
      },
      {
        label: 'Exchanges',
        value: String(agent.stats.totalExchanges.toLocaleString()),
      },
      {
        label: 'Active Applications',
        value: String(agent.stats.activeApplications),
      },
      {
        label: 'Avg Response',
        value:
          agent.stats.avgResponseTime > 0
            ? `${agent.stats.avgResponseTime}s`
            : '—',
      },
    ];
  }, [agent]);

  const timelineVersions: VersionEntry[] = useMemo(() => {
    if (!agent) return [];
    return agent.versions.map((v) => ({
      version: v.version,
      status: v.status,
      date: v.date,
      description: v.description,
      changeCount: v.changeCount,
    }));
  }, [agent]);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!id || id === 'new') {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <PageMessage>
          Use Create Agent on the Agents page to start a new draft.
        </PageMessage>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <PageMessage>Loading agent…</PageMessage>
      </Page>
    );
  }

  if (isError || !agent) {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <PageMessage>Agent not found.</PageMessage>
      </Page>
    );
  }

  const scopeCfg = SCOPE_BADGES[agent.scope] ?? SCOPE_BADGES.organisation;
  const viewed = viewedAgent ?? agent;
  const management = isManagementAgent(agent);
  const openAgent = (idOrName: string) => navigate(`/agents/${idOrName}`);

  return (
    <Page>
      <AgentHeader>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <HeaderTop>
          <HeaderInfo>
            <HeaderRow>
              <Text variant="h3">{agent.name}</Text>
            </HeaderRow>
            {agent.description && (
              <Text
                variant="body1"
                style={{ color: 'var(--text-secondary)', margin: 0 }}
              >
                {agent.description}
              </Text>
            )}
            {management && (
              <SystemLine>
                {agent.fleet?.orchestrationLabel
                  ? `${agent.fleet.orchestrationLabel}. Use Playground or associate this agent with your application.`
                  : 'Management system — use Playground or associate this agent with your application.'}
              </SystemLine>
            )}
            {agent.membership && !management && (
              <SystemLine>
                For production integrations, associate the parent system (
                {agent.membership.parentAgentName}).
              </SystemLine>
            )}
            <BadgeRow>
              <Badge variant={scopeCfg.variant} size="sm">
                {scopeCfg.label}
              </Badge>
              <Badge
                variant={
                  typeBadgeLabel(agent.agentType) === 'Management'
                    ? 'warning'
                    : 'info'
                }
                size="sm"
              >
                {typeBadgeLabel(agent.agentType)}
              </Badge>
              <Badge
                variant={
                  agent.versionStatus === 'published' && !agent.hasOpenDraft
                    ? 'success'
                    : agent.versionStatus === 'sandbox'
                      ? 'warning'
                      : agent.versionStatus === 'draft' || agent.hasOpenDraft
                        ? 'warning'
                        : 'neutral'
                }
                size="sm"
              >
                ● {versionStatusBadgeText(agent)}
              </Badge>
              {(agent.llmModel || agent.config?.model) && (
                <Badge variant="neutral" size="sm">
                  {agent.llmModel || agent.config?.model}
                </Badge>
              )}
              {(() => {
                const recModels =
                  agent.recommendedModels ||
                  agent.config?.recommendedModels ||
                  [];
                return recModels.length > 0 ? (
                  <Badge variant="info" size="sm">
                    {recModels.length} recommended
                  </Badge>
                ) : null;
              })()}
              {(() => {
                const rawEffort =
                  (agent.config as Record<string, unknown> | undefined)
                    ?.effort ??
                  (agent as unknown as Record<string, unknown>).effort;
                const effort =
                  rawEffort === 'low' || rawEffort === 'high'
                    ? rawEffort
                    : 'medium';
                return effort !== 'medium' ? (
                  <Badge variant="warning" size="sm">
                    Effort: {effort}
                  </Badge>
                ) : null;
              })()}
              {readOnly && (
                <Badge variant="neutral" size="sm">
                  Read-only
                </Badge>
              )}
              {agent.membership && (
                <MembershipChip
                  membership={agent.membership}
                  onOpenParent={openAgent}
                />
              )}
            </BadgeRow>
            <HintChips
              agent={agent}
              forceNextSteps={searchParams.get('created') === '1'}
              onOpenPlayground={() => handleTabChange('playground')}
              onAssociate={() => navigate('/applications')}
              onConfigure={() => handleTabChange('configure')}
            />
          </HeaderInfo>
          <HeaderActions>
            {agent.versions.length > 0 && (
              <VersionField>
                Version
                <VersionSelect
                  aria-label="Agent version"
                  value={selectedVersion?.version || agent.versions[0].version}
                  onChange={(e) => selectVersion(e.target.value)}
                >
                  {agent.versions.map((entry) => (
                    <option key={entry.version} value={entry.version}>
                      {versionOptionLabel(entry)}
                    </option>
                  ))}
                </VersionSelect>
              </VersionField>
            )}
            <Button
              variant="outline"
              onClick={handleOpenCreateVersion}
              disabled={readOnly || saving}
              title={
                readOnly
                  ? 'Create New Version is not available for read-only agents'
                  : 'Create a draft from the latest published version'
              }
            >
              Create New Version
            </Button>
            <Button
              variant="primary"
              onClick={handleOpenPublishConfirm}
              disabled={readOnly || saving || !canPublishAgent(agent)}
              title={
                canPublishAgent(agent)
                  ? `Publish ${formatVersionLabel(agent.draftVersion || agent.sandboxVersion || agent.version) || agent.version} as latest`
                  : 'Create a new version before publishing'
              }
            >
              Publish
            </Button>
          </HeaderActions>
        </HeaderTop>
      </AgentHeader>

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

      <Tabs
        items={TABS}
        activeTab={activeTab}
        onChange={handleTabChange}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewPanels>
            <OverviewPanel>
              <div>
                <StatsGrid items={stats} columns={2} />
              </div>
              <MetaSection>
                <SectionLabel as="h4">Agent Details</SectionLabel>
                <MetaList>
                  <MetaItem>
                    <MetaKey>Type</MetaKey>
                    <MetaValue>{typeBadgeLabel(agent.agentType)}</MetaValue>
                  </MetaItem>
                  {management && (
                    <MetaItem>
                      <MetaKey>Orchestration</MetaKey>
                      <MetaValue>
                        {agent.fleet?.orchestrationLabel ||
                          agent.fleet?.orchestrationMode ||
                          '—'}
                      </MetaValue>
                    </MetaItem>
                  )}
                  <MetaItem>
                    <MetaKey>Default model</MetaKey>
                    <MetaValue>
                      {viewed.llmModel || viewed.config?.model || '—'}
                    </MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Effort</MetaKey>
                    <MetaValue>
                      {(() => {
                        const rawEffort =
                          (viewed.config as Record<string, unknown> | undefined)
                            ?.effort ??
                          (viewed as unknown as Record<string, unknown>).effort;
                        return rawEffort === 'low' || rawEffort === 'high'
                          ? rawEffort
                          : 'medium';
                      })()}
                    </MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Created</MetaKey>
                    <MetaValue>{formatDate(agent.createdAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Last Updated</MetaKey>
                    <MetaValue>{formatDate(agent.updatedAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Scope</MetaKey>
                    <MetaValue>{scopeCfg.label}</MetaValue>
                  </MetaItem>
                </MetaList>
                {((viewed.recommendedModels &&
                  viewed.recommendedModels.length > 0) ||
                  (viewed.config?.recommendedModels &&
                    viewed.config.recommendedModels.length > 0)) && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Recommended models
                    </div>
                    <MetaChips>
                      {(
                        viewed.recommendedModels ||
                        viewed.config?.recommendedModels ||
                        []
                      ).map((m) => (
                        <Badge key={m} variant="neutral" size="sm">
                          {m}
                        </Badge>
                      ))}
                    </MetaChips>
                    <Text
                      variant="body1"
                      style={{
                        margin: '0.5rem 0 0',
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Playground and API may pass <code>model</code> to override
                      the default for one exchange.
                    </Text>
                  </div>
                )}
              </MetaSection>
              {agent.capabilities && agent.capabilities.length > 0 && (
                <MetaSection>
                  <SectionLabel as="h4">Capabilities</SectionLabel>
                  <CapabilitiesRow capabilities={agent.capabilities} />
                  <MetaChips style={{ marginTop: '0.75rem' }}>
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Configure models & sources
                    </SoftChip>
                  </MetaChips>
                </MetaSection>
              )}
              {(!agent.capabilities || agent.capabilities.length === 0) && (
                <MetaSection>
                  <MetaChips>
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Configure models & sources
                    </SoftChip>
                  </MetaChips>
                </MetaSection>
              )}
            </OverviewPanel>

            <OverviewPanel>
              {management && agent.fleet && (
                <AgentSystemSummary
                  fleet={agent.fleet}
                  onOpenAgent={openAgent}
                  managerLabel={agent.name}
                />
              )}
              {agent.membership && !management && (
                <PartOfSystemCard
                  membership={agent.membership}
                  onOpenParent={openAgent}
                />
              )}
              <MetaSection>
                <SectionLabel as="h4">Version History</SectionLabel>
                {timelineVersions.length > 0 ? (
                  <VersionTimeline
                    versions={timelineVersions}
                    activeVersion={selectedVersion?.version}
                    onSelect={selectVersion}
                  />
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No version history yet.
                  </Text>
                )}
              </MetaSection>
              <MetaSection>
                <SectionLabel as="h4">Application Associations</SectionLabel>
                {agent.applications.length > 0 ? (
                  <div>
                    {agent.applications.map((app) => (
                      <PartnerRow key={app.id}>
                        <span style={{ fontWeight: 500 }}>{app.name}</span>
                        <Badge
                          variant={
                            String(app.status).toLowerCase() === 'active'
                              ? 'success'
                              : 'neutral'
                          }
                          size="sm"
                        >
                          {app.status}
                        </Badge>
                      </PartnerRow>
                    ))}
                  </div>
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    {management
                      ? 'No applications associated. Prefer associating this management agent for production.'
                      : agent.membership
                        ? 'No direct app associations. Prefer associating the parent system for production.'
                        : 'No applications associated.'}
                  </Text>
                )}
              </MetaSection>
              <MetaSection>
                <SectionLabel as="h4">Data sources</SectionLabel>
                {agent.dataSources && agent.dataSources.length > 0 ? (
                  <div>
                    {agent.dataSources.map((ds) => (
                      <PartnerRow key={ds.dataSourceId}>
                        <Link
                          to={dataSourceInstancePath(ds.dataSourceId)}
                          style={{ fontWeight: 500, color: 'inherit' }}
                        >
                          {ds.name}
                        </Link>
                        <Badge variant="info" size="sm">
                          {ds.dsType || ds.status || 'Data source'}
                        </Badge>
                      </PartnerRow>
                    ))}
                  </div>
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No data sources associated.
                  </Text>
                )}
                <MetaChips style={{ marginTop: '0.5rem' }}>
                  {!readOnly && (
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Associate a data source
                    </SoftChip>
                  )}
                </MetaChips>
              </MetaSection>
              <MetaSection>
                <SectionLabel as="h4">Custom MCP</SectionLabel>
                {agent.mcpServers && agent.mcpServers.length > 0 ? (
                  <div>
                    {agent.mcpServers.map((row) => (
                      <PartnerRow key={row.mcpId}>
                        <Link
                          to={mcpInstancePath(row.mcpId)}
                          style={{ fontWeight: 500, color: 'inherit' }}
                        >
                          {row.name}
                        </Link>
                        <Badge variant="info" size="sm">
                          {row.authMode === 'stored_credentials'
                            ? 'Stored credentials'
                            : 'Caller token'}
                        </Badge>
                      </PartnerRow>
                    ))}
                  </div>
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No Custom MCP servers associated.
                  </Text>
                )}
                <MetaChips style={{ marginTop: '0.5rem' }}>
                  {!readOnly && (
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Associate a Custom MCP
                    </SoftChip>
                  )}
                </MetaChips>
              </MetaSection>
            </OverviewPanel>
          </OverviewPanels>
        )}

        {activeTab === 'configure' && (
          <ConfigureTab
            agent={viewed}
            catalog={catalogAgents}
            readOnly={readOnly}
            snapshotLabel={snapshotLabelFor(selectedVersion)}
            onOpenWorkingVersion={() => {
              const working = agent.versions.find(
                (entry) =>
                  entry.status === 'draft' || entry.status === 'sandbox',
              );
              if (working) selectVersion(working.version);
            }}
            onCreateVersion={handleOpenCreateVersion}
          />
        )}

        {activeTab === 'sessions' && (
          <SessionsTab
            agentId={agent.id}
            agentName={agent.agentName}
            onNewSession={startNewPlaygroundSession}
            onOpenInPlayground={openPlaygroundWithSession}
          />
        )}

        {activeTab === 'analytics' && (
          <ScopedAnalyticsPanel
            scope="agent"
            scopeLabel={agent.agentName}
            agentName={agent.agentName}
            onOpenPlayground={() => handleTabChange('playground')}
          />
        )}

        {activeTab === 'playground' && (
          <PlaygroundLayout>
            <ChatContainer>
              <ChatBanner>
                <StatusDot status="info" size="sm" />
                {agent.versionStatus === 'draft'
                  ? 'Draft agents may not be available for chat until published'
                  : management
                    ? sessionId
                      ? `System sandbox · ${agent.agentName} · session ${sessionId.slice(0, 8)}…`
                      : `System sandbox · ${agent.agentName} (fleet invoke)`
                    : sessionId
                      ? `Sandbox chat · ${agent.agentName} · session ${sessionId.slice(0, 8)}…`
                      : `Sandbox chat · ${agent.agentName}`}
                <ModelPickerTrigger
                  type="button"
                  aria-label="Playground model"
                  aria-haspopup="dialog"
                  aria-expanded={modelPickerOpen}
                  title="Override model for this exchange"
                  onClick={() => setModelPickerOpen(true)}
                >
                  {playgroundModelTriggerLabel}
                </ModelPickerTrigger>
                {showPlaygroundEffort && (
                <ModelSelect
                  aria-label="Effort level"
                  value={effortOverride}
                  onChange={(e) =>
                    setEffortOverride(
                      e.target.value as '' | 'low' | 'medium' | 'high',
                    )
                  }
                  title="Effort level for reasoning depth"
                >
                  <option value="">Effort: Default</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </ModelSelect>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={startNewPlaygroundSession}
                >
                  New session
                </Button>
              </ChatBanner>
              <Modal
                isOpen={modelPickerOpen}
                onClose={closeModelPicker}
                title="Select model"
              >
                <ModelPicker
                  models={playgroundModelCatalog}
                  query={playgroundModelSearch}
                  onQueryChange={setPlaygroundModelSearch}
                  onSelect={selectPlaygroundModel}
                  selectedId={modelOverride}
                  listLabel="Playground models"
                  pinned={[
                    {
                      id: 'default',
                      label: `Default (${agent.llmModel || agent.config?.model || '—'})`,
                      selected: !modelOverride,
                    },
                    {
                      id: 'auto',
                      label: 'Auto',
                      selected: modelOverride === 'auto',
                    },
                  ]}
                  onPinnedSelect={(id) =>
                    selectPlaygroundModel(id === 'default' ? '' : id)
                  }
                />
              </Modal>
              <MessageList>
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} $role={msg.role}>
                    {msg.content}
                  </MessageBubble>
                ))}
                {showPending && (
                  <PendingBubble
                    data-testid="playground-pending"
                    role="status"
                    aria-live="polite"
                  >
                    <PendingDots aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </PendingDots>
                    Waiting for response…
                  </PendingBubble>
                )}
                <div ref={messagesEndRef} />
              </MessageList>
              <ChatInputRow onSubmit={handleSendMessage}>
                <ChatInput
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type a message..."
                  disabled={showPending}
                />
                <VoiceMicButton
                  recording={voiceState === 'recording'}
                  onClick={
                    voiceState === 'recording' ? stopRecording : startRecording
                  }
                  disabled={
                    showPending ||
                    voiceState === 'transcribing' ||
                    voiceState === 'speaking'
                  }
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={
                    !chatInput.trim() ||
                    showPending ||
                    missingRequiredFilters.length > 0 ||
                    missingMcpTokens.length > 0
                  }
                >
                  Send
                </Button>
              </ChatInputRow>
              {(voiceState === 'recording' ||
                voiceState === 'transcribing' ||
                voiceState === 'speaking' ||
                voiceError) && (
                <div style={{ padding: '0 1rem 0.75rem' }}>
                  <VoiceChatBar
                    voiceState={voiceState}
                    error={voiceError}
                    onStopSpeaking={stopSpeaking}
                  />
                </div>
              )}
            </ChatContainer>

            <InspectorPanel>
              <InspectorSection>
                <InspectorTitle>Session info</InspectorTitle>
                <Text variant="body1" style={{ margin: 0, fontSize: '0.8125rem' }}>
                  Session: {sessionId || '—'}
                  <br />
                  Agent: {agent.agentName} ({agent.versionStatus})
                  <br />
                  Model:{' '}
                  {modelOverride
                    ? `${modelOverride} · override`
                    : `${agent.llmModel || agent.config?.model || '—'} · default`}
                  <br />
                  Effort: {effortOverride || 'default (medium)'}
                  <br />
                  Version pin:{' '}
                  {playgroundAgentVersion(agent) || 'published (latest)'}
                  <br />
                  {resolvedVersion && (
                    <>Resolved version: {resolvedVersion}<br /></>
                  )}
                  {resolvedModel && (
                    <>Resolved model: {resolvedModel}<br /></>
                  )}
                  {autoModelReason && (
                    <>
                      <span data-testid="playground-auto-reason">{autoModelReason}</span>
                      <br />
                    </>
                  )}
                  {resolvedEffort && (
                    <>Resolved effort: {resolvedEffort}<br /></>
                  )}
                  Messages: {messages.length}
                  <br />
                  WebSocket: {isConnected ? 'connected' : connectionState}
                </Text>
              </InspectorSection>
              {dsFilterParams.length > 0 && (
                <InspectorSection data-testid="playground-query-filters">
                  <InspectorTitle>Query filters</InspectorTitle>
                  <Text
                    variant="body1"
                    style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    Impersonate consumer identity and extra parameters used to
                    scope data-source searches. Required fields must be set
                    before sending.
                  </Text>
                  {searchScope ? (
                    <FilterScopeNote data-testid="playground-search-scope">
                      Applied on every search:{' '}
                      {searchScope}
                    </FilterScopeNote>
                  ) : null}
                  {dsFilterParams.map((param) => {
                    const source = param.source || param.name;
                    const requiredMark = param.required ? ' *' : '';
                    if (source === 'consumer_org_id') {
                      return (
                        <Input
                          key={param.name}
                          label={`${param.label || 'Consumer organisation'}${requiredMark}`}
                          value={consumerOrgId}
                          onChange={(e) => setConsumerOrgId(e.target.value)}
                          placeholder="consumer-org-id"
                        />
                      );
                    }
                    if (source === 'consumer_user_id') {
                      return (
                        <Input
                          key={param.name}
                          label={`${param.label || 'Consumer user'}${requiredMark}`}
                          value={consumerUserId}
                          onChange={(e) => setConsumerUserId(e.target.value)}
                          placeholder="consumer-user-id"
                        />
                      );
                    }
                    const key = source.startsWith('parameters.')
                      ? source.slice('parameters.'.length)
                      : param.name;
                    const listHint = paramUsesListBinding(key, dsFilterConfigs);
                    return (
                      <Input
                        key={param.name}
                        label={`${param.label || key}${requiredMark}`}
                        value={queryParameters[key] || ''}
                        onChange={(e) =>
                          setQueryParameters((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        placeholder={
                          listHint ? `${key} (comma-separated)` : key
                        }
                      />
                    );
                  })}
                </InspectorSection>
              )}
              {playgroundMcps.length > 0 && (
                <InspectorSection>
                  <InspectorTitle>Custom MCP tokens</InspectorTitle>
                  <Text variant="body2" style={{ color: 'var(--text-muted)' }}>
                    Required before Send. Values stay in this session and are
                    never shown in the LLM log.
                  </Text>
                  {playgroundMcps.map((row) => (
                    <Input
                      key={row.mcpId}
                      type="password"
                      label={`${mcpTokenLabel(row)} *`}
                      value={mcpTokenValues[row.mcpId] || ''}
                      onChange={(e) =>
                        setMcpTokenValues((prev) => ({
                          ...prev,
                          [row.mcpId]: e.target.value,
                        }))
                      }
                      autoComplete="off"
                      data-testid={`playground-mcp-token-${row.mcpId}`}
                    />
                  ))}
                </InspectorSection>
              )}
              <InspectorSection>
                <InspectorTitle>LLM call log</InspectorTitle>
                <EventLog>
                  {llmLog.length === 0 && <li>No LLM events yet.</li>}
                  {llmLog.map((line, idx) => (
                    <li key={`${line}-${idx}`}>{line}</li>
                  ))}
                </EventLog>
              </InspectorSection>
              <InspectorSection>
                <InspectorTitle>WebSocket events</InspectorTitle>
                <EventLog>
                  {wsEvents.length === 0 && <li>Subscribe after the first message.</li>}
                  {wsEvents.map((ev, idx) => (
                    <li key={`${ev.type}-${idx}`}>
                      {ev.timestamp ? `${ev.timestamp} · ` : ''}
                      {ev.type?.startsWith('datasource.query.')
                        ? formatDatasourceQueryLog(ev)
                        : ev.type?.startsWith('code.execution.')
                          ? formatCodeExecutionLog(ev)
                          : ev.type?.startsWith('mcp.tool.')
                          ? formatMcpToolLog(ev)
                          : ev.type}
                    </li>
                  ))}
                </EventLog>
              </InspectorSection>
              <InspectorSection>
                <InspectorTitle>System prompt</InspectorTitle>
                <JsonPre style={{ fontSize: '0.75rem', maxHeight: 120, overflow: 'auto' }}>
                  {config.systemPrompt || '—'}
                </JsonPre>
              </InspectorSection>
            </InspectorPanel>
          </PlaygroundLayout>
        )}
      </TabContent>

      <Modal
        isOpen={createVersionOpen}
        onClose={handleCloseCreateVersion}
        title="Create New Version"
      >
        <ModalForm>
          <ModalCopy>
            Clones the latest published config into a Draft. You will edit it on
            Configure with Agent Builder.
          </ModalCopy>
          <Input
            label="Version number"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="e.g. 3.1"
          />
          <Input
            label="What is the expected change?"
            value={newVersionNote}
            onChange={(e) => setNewVersionNote(e.target.value)}
            placeholder="What is the expected change?"
          />
          <ModalActions>
            <Button
              variant="ghost"
              onClick={handleCloseCreateVersion}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateVersion}
              disabled={!newVersion.trim() || saving}
            >
              {saving ? 'Creating…' : 'Create & configure'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <Modal
        isOpen={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        title="Publish version?"
      >
        <ModalForm>
          <ModalCopy>
            Are you sure you want to publish{' '}
            <strong>
              {formatVersionLabel(
                agent.draftVersion ||
                  agent.sandboxVersion ||
                  agent.version,
              ) || agent.version}
            </strong>
            ? It will become the latest version. API callers who omit{' '}
            <code>agent-version</code> will use this version.
          </ModalCopy>
          <ModalActions>
            <Button
              variant="ghost"
              onClick={() => setPublishConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={saving}
            >
              {saving ? 'Publishing…' : 'Yes, publish'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default AgentDetail;
